import type { PartialCatalogue } from './catalogues/fr';

export type TextDirection = 'ltr' | 'rtl';

export interface LanguageEntry {
  readonly code: string;
  /**
   * The language named in itself. Someone who reads only Arabic has to find العربية in a list they
   * cannot otherwise parse, so this is never translated: it is the one thing on the row that a
   * reader of that language is certain to recognise.
   */
  readonly nativeName: string;
  /**
   * A flag to find the row by, at a glance and from across the language barrier.
   *
   * It names a country, never the language — no country owns a language, and Arabic, Spanish,
   * Portuguese, German and Dutch are each read in many. So the flag is decorative throughout,
   * hidden from screen readers, and the name beside it is what actually says which language this
   * is. Where a language has no one country, the choice is a convention and nothing more.
   *
   * Written as the emoji pair, drawn by the flag face the app carries with its other fonts.
   */
  readonly flag: string;
  readonly dir: TextDirection;
  /**
   * Pulled in on demand, so a language costs nothing until it is chosen. French is the exception:
   * it is imported statically as the source language, the first render and the fallback.
   */
  readonly load: () => Promise<PartialCatalogue>;
}

/**
 * The one place a language is declared. Adding one means writing its catalogue file and adding a
 * line here — nothing else in the app knows the list, and nothing tests a code by hand to decide a
 * direction.
 *
 * This is also the order the reader sees them in, and it is curated rather than alphabetical:
 * Arabic opens the list, then the two languages the app was written in, then the others as they
 * arrive — Nederlands, Türkçe, Español, Deutsch, Português, Italiano, Svenska. A language is
 * declared here only once it has a catalogue: a line with nothing behind it would offer a reader
 * a way out that leads straight back to French.
 */
export const LANGUAGES: readonly LanguageEntry[] = Object.freeze([
  Object.freeze({
    code: 'ar',
    nativeName: 'العربية',
    // Convention, not a claim: Arabic belongs to no single country.
    flag: '🇸🇦',
    dir: 'rtl' as const,
    load: async () => (await import('./catalogues/ar')).default,
  }),
  Object.freeze({
    code: 'fr',
    nativeName: 'Français',
    flag: '🇫🇷',
    dir: 'ltr' as const,
    load: async () => (await import('./french-catalogue')).loadFrenchComplete(),
  }),
  Object.freeze({
    code: 'en',
    nativeName: 'English',
    flag: '🇬🇧',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/en')).default,
  }),
  Object.freeze({
    code: 'nl',
    nativeName: 'Nederlands',
    flag: '🇳🇱',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/nl')).default,
  }),
  Object.freeze({
    code: 'tr',
    nativeName: 'Türkçe',
    flag: '🇹🇷',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/tr')).default,
  }),
  Object.freeze({
    code: 'es',
    nativeName: 'Español',
    flag: '🇪🇸',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/es')).default,
  }),
  Object.freeze({
    code: 'de',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/de')).default,
  }),
  Object.freeze({
    code: 'pt',
    nativeName: 'Português',
    flag: '🇵🇹',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/pt')).default,
  }),
  Object.freeze({
    code: 'it',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/it')).default,
  }),
  Object.freeze({
    code: 'sv',
    nativeName: 'Svenska',
    flag: '🇸🇪',
    dir: 'ltr' as const,
    load: async () => (await import('./catalogues/sv')).default,
  }),
  // Urdu, last (Yaya, 20/10): Pakistan and its diaspora. Right to left, in Nastaliq (styles.scss).
  Object.freeze({
    code: 'ur',
    nativeName: 'اردو',
    flag: '🇵🇰',
    dir: 'rtl' as const,
    load: async () => (await import('./catalogues/ur')).default,
  }),
]);

/** The source language: complete, validated, and what every missing key falls back to. */
export const SOURCE_LANGUAGE = 'fr';

export const languageCodes: readonly string[] = Object.freeze(
  LANGUAGES.map((language) => language.code),
);

export const findLanguage = (code: string | null): LanguageEntry | null =>
  LANGUAGES.find((language) => language.code === code) ?? null;

export const isSupportedLanguage = (code: string | null): boolean => findLanguage(code) !== null;

/** Direction comes from the registry, never from a test on the code itself. */
export const directionOf = (code: string): TextDirection => findLanguage(code)?.dir ?? 'ltr';
